import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Modal, Input } from "barte-design-system";
import { FloppyDiskIcon, InfoIcon } from "@phosphor-icons/react";
import { useValidationErrors } from "@payfac/hooks";

import { objectShape } from "./modal-confirm-save.constants";
import styles from "./modal-confirm-save.module.css";
import type { ModalConfirmSaveProps } from "./modal-confirm-save.type";

export const ModalConfirmSave = ({
    isOpen,
    onClose,
    onConfirm,
    isLoading,
}: ModalConfirmSaveProps) => {
    const { t } = useTranslation();
    const [description, setDescription] = useState("");

    const { isValid, errors, onHandleListenErrors } = useValidationErrors(
        objectShape(t),
        { description },
    );

    useEffect(() => {
        if (!isOpen) {
            setDescription("");
            onHandleListenErrors(false)
        }
    }, [isOpen]);

    const handleSave = () => {
        if (!isValid) {
            onHandleListenErrors(true);
            return;
        }

        onConfirm(description);
    };

    const title = (
        <div className={styles["modal-header"]}>
            <FloppyDiskIcon weight="regular" /> {t("backoffice.pageModalCompanyPlanModalConfirmSaveTitle")}
        </div>
    ) as unknown as string;

    const observationLabel = (
        <div className={styles["observation-label"]}>
            <InfoIcon weight="regular" /> {t("backoffice.pageModalCompanyPlanModalConfirmSaveLabel")}
        </div>
    ) as unknown as string;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={title}
            size="sm"
            footer={{
                primaryButton: {
                    children: t("backoffice.pageModalCompanyPlanModalConfirmSaveButton"),
                    onClick: handleSave,
                    disabled: isLoading,
                    loading: isLoading,
                },
                secondaryButton: {
                    children: t("backoffice.pageModalCompanyPlanCancelButton"),
                    variant: "outline",
                    onClick: onClose,
                    disabled: isLoading,
                },
                buttonLayout: "hug",
            }}
        >
            <div className={styles.content}>
                <p className={styles.description}>
                    {t("backoffice.pageModalCompanyPlanModalConfirmSaveDescription")}
                </p>

                <div className={styles.field}>
                    <Input
                        type="textarea"
                        label={observationLabel}
                        placeholder={t("backoffice.pageModalCompanyPlanModalConfirmSavePlaceholder")}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        disabled={isLoading}
                        rows={5}
                        error={!!errors?.description}
                        helpText={errors?.description}
                    />
                </div>
            </div>
        </Modal>
    );
};
